import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTrackingEvents } from '@/hooks/useTrackingEvents';
import { useDocuments } from '@/hooks/useDocuments';
import { useOrderRating } from '@/hooks/useOrderRating';
import { useRealTimeTracking } from '@/hooks/useRealTimeTracking';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Package, MapPin, Calendar, Truck, CheckCircle, AlertCircle, Clock, Star } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { MapView } from '@/components/tracking/MapView';
import { DocumentList } from '@/components/driver/DocumentList';
import { RatingDialog } from '@/components/rating/RatingDialog';
import { RatingDisplay } from '@/components/rating/RatingDisplay';
import { useTranslation } from 'react-i18next';

const statusConfig = {
  draft: { label: 'Brouillon', color: 'bg-[#87ADD4]/10 text-[#5A6B7A]' },
  posted: { label: 'Publiée', color: 'bg-[#0880AE]/10 text-[#0880AE]' },
  booked: { label: 'Réservée', color: 'bg-[#F2AC57]/10 text-[#F2AC57]' },
  assigned: { label: 'Assignée', color: 'bg-[#37699E]/10 text-[#37699E]' },
  in_transit: { label: 'En transit', color: 'bg-[#FFBF07]/10 text-[#2D3748]' },
  delivered: { label: 'Livrée', color: 'bg-[#14A38B]/10 text-[#14A38B]' },
  cancelled: { label: 'Annulée', color: 'bg-[#FF7171]/10 text-[#FF7171]' },
};

const eventTypeConfig = {
  pickup_confirmed: { label: 'Enlèvement confirmé', icon: CheckCircle, color: 'text-[#14A38B]' },
  in_transit: { label: 'En transit', icon: Truck, color: 'text-[#FFBF07]' },
  delivered: { label: 'Livré', icon: CheckCircle, color: 'text-[#14A38B]' },
  exception: { label: 'Exception', icon: AlertCircle, color: 'text-[#FF7171]' },
};

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { events, isLoading: eventsLoading } = useTrackingEvents(orderId);
  const { documents, isLoading: documentsLoading } = useDocuments(orderId || '');
  const { rating, submitRating, isSubmitting } = useOrderRating(orderId);
  
  // Enable real-time tracking updates
  useRealTimeTracking(orderId);

  const { data: order, isLoading: orderLoading } = useQuery({
    queryKey: ['order', orderId],
    queryFn: async () => {
      if (!orderId) throw new Error('No order ID');

      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', orderId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
  });

  if (orderLoading || eventsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#DBE2EA] border-t-[#0880AE] rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm text-[#5A6B7A] font-['Funnel_Sans']">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <AlertCircle size={48} className="text-[#FF7171] mx-auto" />
          <p className="text-lg text-[#2D3748] font-['Funnel_Sans']">Commande introuvable</p>
          <Button onClick={() => navigate('/shipper/dashboard')}>Retour au tableau de bord</Button>
        </div>
      </div>
    );
  }

  const status = statusConfig[order.status as keyof typeof statusConfig];
  const isDelivered = order.status === 'delivered';
  const canRate = isDelivered && !rating?.shipper_rating;
  const hasRated = !!rating?.shipper_rating;

  const handleSubmitRating = async (ratingValue: number, comment: string) => {
    if (!orderId) return;
    await submitRating({
      orderId,
      rating: ratingValue,
      comment,
      ratingType: 'shipper',
    });
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/shipper/dashboard')}
            className="mb-4 text-[#0880AE] hover:bg-[#EBF4F8] transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>

          <div className="bg-white rounded-xl shadow-md p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                  {order.order_number}
                </h1>
                <p className="text-sm text-[#5A6B7A] mt-1 font-['Funnel_Sans']">
                  {order.cargo_title || 'Suivi de commande'}
                </p>
              </div>
              <Badge className={`${status.color} text-xs font-medium px-3 py-1.5 rounded-md`}>
                {status.label}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-[#DBE2EA]">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="text-[#0880AE] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] uppercase tracking-wide">Enlèvement</p>
                    <p className="text-sm text-[#2D3748] font-['Funnel_Sans'] mt-1">{order.pickup_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="text-[#5A6B7A] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                      {format(new Date(order.pickup_date), 'dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <MapPin className="text-[#14A38B] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans'] uppercase tracking-wide">Livraison</p>
                    <p className="text-sm text-[#2D3748] font-['Funnel_Sans'] mt-1">{order.delivery_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="text-[#5A6B7A] flex-shrink-0 mt-1" size={18} />
                  <div>
                    <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                      {format(new Date(order.delivery_date), 'dd MMMM yyyy', { locale: fr })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-4 border-t border-[#DBE2EA]">
              <Package className="text-[#5A6B7A]" size={18} />
              <span className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                {order.weight_kg} kg
              </span>
              {order.pallets_count && (
                <>
                  <span className="text-[#5A6B7A]">•</span>
                  <span className="text-sm text-[#2D3748] font-['Funnel_Sans']">
                    {order.pallets_count} palettes
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          {/* Documents Section */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-6">
              Documents de livraison
            </h2>
            {documentsLoading ? (
              <div className="text-center py-8">
                <div className="w-8 h-8 border-4 border-[#DBE2EA] border-t-[#0880AE] rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <DocumentList documents={documents} />
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-6">
                Historique du suivi
              </h2>

            {events.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="text-[#87ADD4] mx-auto mb-3" size={48} />
                <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                  Aucun événement de suivi pour le moment
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {events.map((event, index) => {
                  const eventConfig = eventTypeConfig[event.event_type];
                  const Icon = eventConfig.icon;

                  return (
                    <div key={event.id} className="relative">
                      {/* Vertical line */}
                      {index < events.length - 1 && (
                        <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-[#DBE2EA]" />
                      )}

                      <div className="flex gap-4">
                        <div className={`w-10 h-10 rounded-full bg-white border-2 border-[#DBE2EA] flex items-center justify-center flex-shrink-0 ${eventConfig.color}`}>
                          <Icon size={20} />
                        </div>

                        <div className="flex-1 pb-6">
                          <div className="flex items-start justify-between mb-1">
                            <p className="text-sm font-medium text-[#2D3748] font-['Funnel_Sans']">
                              {eventConfig.label}
                            </p>
                            <p className="text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                              {format(new Date(event.event_time), 'dd MMM yyyy, HH:mm', { locale: fr })}
                            </p>
                          </div>

                          {event.notes && (
                            <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans'] mt-2">
                              {event.notes}
                            </p>
                          )}

                          {event.photo_url && (
                            <img
                              src={event.photo_url}
                              alt="Photo de l'événement"
                              className="mt-3 rounded-lg w-full max-w-xs object-cover"
                            />
                          )}

                          {(event.latitude && event.longitude) && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-[#5A6B7A] font-['Funnel_Sans']">
                              <MapPin size={12} />
                              <span>
                                {event.latitude.toFixed(4)}, {event.longitude.toFixed(4)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                </div>
              )}
            </div>

            {/* Map */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-6">
                Carte de suivi
              </h2>
              <MapView 
                pickupAddress={order.pickup_address || 'Adresse d\'enlèvement'}
                deliveryAddress={order.delivery_address || 'Adresse de livraison'}
                pickupLat={order.pickup_lat ?? undefined}
                pickupLng={order.pickup_lng ?? undefined}
                deliveryLat={order.delivery_lat ?? undefined}
                deliveryLng={order.delivery_lng ?? undefined}
                trackingEvents={events.map(e => ({
                  latitude: e.latitude ?? 0,
                  longitude: e.longitude ?? 0,
                  event_type: e.event_type,
                  event_time: e.event_time || new Date().toISOString(),
                  notes: e.notes ?? undefined,
                }))}
                showRoute={true}
              />
            </div>
          </div>

          {/* Rating Section - Only show for delivered orders */}
          {isDelivered && (
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-xl font-medium text-[#37699E] font-['Lexend_Deca'] mb-6 flex items-center gap-2">
                <Star className="text-[#FFBF07]" size={24} />
                {t('rating.title')}
              </h2>

              {hasRated ? (
                <div className="space-y-4">
                  <div className="bg-[#14A38B]/10 border border-[#14A38B]/20 rounded-lg p-4 mb-4">
                    <p className="text-sm text-[#14A38B] font-medium flex items-center gap-2">
                      <CheckCircle size={16} />
                      {t('rating.success')}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-[#5A6B7A] uppercase tracking-wide mb-3">
                      {t('rating.yourRating')}
                    </h3>
                    <RatingDisplay
                      rating={rating.shipper_rating!}
                      comment={rating.shipper_comment}
                      ratedAt={rating.shipper_rated_at}
                    />
                  </div>
                </div>
              ) : canRate ? (
                <div className="space-y-4">
                  <p className="text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                    {t('rating.question')}
                  </p>
                  <RatingDialog
                    trigger={
                      <Button className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold w-full">
                        <Star className="mr-2 h-4 w-4" />
                        {t('rating.rateDelivery')}
                      </Button>
                    }
                    onSubmit={handleSubmitRating}
                    isSubmitting={isSubmitting}
                  />
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}